// frontend/src/App.js
import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

/* ─── global toast context ─── */
import { NotificationProvider } from './context/NotificationContext'
import ErrorBoundary from './components/ErrorBoundary'

/* ─── scroll helper ─── */
import ScrollToTop from './components/ScrollToTop'

/* ─── live-chat widget ─── */
import ChatBot from './components/ChatBot'

/* ─── public layout & main pages ─── */
import Layout              from './layout/Layout'
import Home                from './pages/Home'
import Login               from './pages/Login'
import Register            from './pages/Register'
import ForgotPassword      from './pages/ForgotPassword'
import ResetPassword       from './pages/ResetPassword'
import PostScheduling      from './pages/PostScheduling'
import AIPostCreation      from './pages/AIPostCreation'
import BulkPosting         from './pages/BulkPosting'
import LinkInBio           from './pages/LinkInBio'
import SupportArticle      from './pages/support/SupportArticle'
import PlatformScheduler   from './pages/PlatformScheduler'
import UtmBuilder          from './pages/tools/UtmBuilder'
import EngagementRatePage  from './pages/EngagementRatePage'
import BioGeneratorPage    from './pages/BioGeneratorPage'
import CaptionGeneratorPage from './pages/CaptionGeneratorPage'
import SizePage           from './pages/SizePage'
import NameGeneratorPage  from './pages/NameGeneratorPage'
import LinkedInFormatterPage from './pages/LinkedInFormatterPage'
import EmojiTranslatorPage from './pages/EmojiTranslatorPage'
import GridPlannerPage    from './pages/GridPlannerPage'
import PersonaHub         from './pages/PersonaHub'
import ToolsHub           from './pages/ToolsHub'
import ContentCalendarTemplatePage from './pages/ContentCalendarTemplatePage'
import SocialReportTemplatePage from './pages/SocialReportTemplatePage'
import PersonaPage         from './pages/PersonaPage'
import BestTimePage        from './pages/BestTimePage'
import NotFound            from './pages/NotFound'
import VideoPhotoEditor    from './pages/VideoPhotoEditor'
import CalendarView        from './pages/CalendarView'
import PlansPricing        from './pages/PlansPricing'
import ContactUs           from './pages/ContactUs'

/* ─── blog pages ─── */
import BlogHome            from './pages/blog/BlogHome'
import BlogPost            from './pages/blog/BlogPost'

/* ─── company pages ─── */
import About               from './pages/About'
import Careers             from './pages/Careers'
import Press               from './pages/Press'
import AffiliateProgram    from './pages/AffiliateProgram'
import PrivacyPolicy       from './pages/PrivacyPolicy'
import TermsPolicy         from './pages/TermsPolicy'
import SafetyPolicy from './pages/SafetyPolicy';
// --- 1. IMPORT YOUR NEW COMPONENT ---
import ApiDocumentationPage from './pages/ApiDocumentationPage';
import EmailDomainSetupPage from './pages/EmailDomainSetupPage';


/* ─── dashboard core ─── */
import DashboardLayout     from './pages/dashboard/DashboardLayout'
import Post                from './pages/dashboard/post/Post'
import Accounts            from './pages/dashboard/accounts/Accounts'
import Files               from './pages/dashboard/Files'
import Calendar            from './pages/dashboard/calendar/CalendarPage'
import Profile             from './pages/dashboard/Profile'
import AdminPage           from './pages/dashboard/AdminPage'
import AnalyticsPage       from './pages/dashboard/analytics/AnalyticsPage'
import LinkInBioPage       from './pages/dashboard/linkinbio/LinkInBioPage'
import QuickstartPage      from './pages/dashboard/QuickstartPage'
import StudioLayout        from './pages/dashboard/studio/StudioLayout'
import AIBulkStudioPage    from './pages/dashboard/studio/AIBulkStudioPage'
import PublicBioPage       from './pages/bio/PublicBioPage'
import MarketingLayout     from './pages/dashboard/marketing/MarketingLayout'
import CRMPage             from './pages/dashboard/marketing/CRMPage'
import EmailLayout         from './pages/dashboard/marketing/email/EmailLayout'
import EmailMarketingPage  from './pages/dashboard/marketing/EmailMarketingPage'
import EmailLists          from './pages/dashboard/marketing/email/EmailLists'
import EmailAutomations    from './pages/dashboard/marketing/email/EmailAutomations'
import EmailSettings       from './pages/dashboard/marketing/email/EmailSettings'

/* ─── subscription / plans & credits (dashboard) ─── */
import PlansPage           from './pages/dashboard/subscription/PlansPage'
import CreditsPage         from './pages/dashboard/subscription/CreditsPage'
import VideoCreditsPage    from './pages/dashboard/subscription/VideoCreditsPage'

/* ─── settings subtree ─── */
import SettingsLayout              from './pages/dashboard/settings/SettingsLayout'
import AccountSettingsPage         from './pages/dashboard/settings/AccountSettingsPage'
import SubscriptionSettingsPage    from './pages/dashboard/settings/SubscriptionSettingsPage'
import LogoutPage                  from './pages/dashboard/settings/LogoutPage'
import ApiKeysPage                 from './pages/dashboard/settings/ApiKeysPage'
import HashtagSettingsPage         from './pages/dashboard/settings/HashtagSettingsPage'


/* ─── auth guard ─── */
import ProtectedRoute from './components/ProtectedRoute'

function App () {
  /* ─── GA4 page-view hook ─── */
  const location = useLocation()

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        page_title: document.title
      })
    }
  }, [location])

  return (
    <NotificationProvider>
      <ScrollToTop />

      {/* Chat bubble sits outside Routes so it’s present on every page */}
      <ChatBot />

      <ErrorBoundary>
      <Routes>

        {/* ── Public link-in-bio page (standalone, no nav/footer) ── */}
        <Route path='/u/:username' element={<PublicBioPage />} />

        {/* ── Public ── */}
        <Route element={<Layout />}>
          <Route path='/'                element={<Home />} />
          <Route path='/login'             element={<Login />} />
          <Route path='/register'          element={<Register />} />
          <Route path='/forgot-password'   element={<ForgotPassword />} />
          <Route path='/reset-password/:token' element={<ResetPassword />} />

          {/* Feature marketing pages */}
          <Route path='/post-scheduling'    element={<PostScheduling />} />
          <Route path='/ai-post-creation'   element={<AIPostCreation />} />
          <Route path='/bulk-posting'       element={<BulkPosting />} />
          <Route path='/link-in-bio'        element={<LinkInBio />} />

          {/* Support articles inherited from the pre-rebuild site. These URLs are
              still indexed and ranking; before this route they fell through to the
              SPA fallback and rendered an empty page. */}
          <Route path='/p/:slug'            element={<SupportArticle />} />

          {/* Platform scheduler pages. URLs match the target intent —
              'instagram scheduling tool' $7.41 / comp 5, 'tiktok scheduling
              tool' $11.71 / comp 1 — rather than sitting under /bulk-posting/. */}
          <Route path='/instagram-scheduler' element={<PlatformScheduler />} />
          <Route path='/tiktok-scheduler'    element={<PlatformScheduler />} />
          <Route path='/linkedin-scheduler'  element={<PlatformScheduler />} />

          {/* Free tools. Fully usable without an account — gating them would
              earn no links and no rankings, which is the point of building them. */}
          <Route path='/tools/utm-builder'   element={<UtmBuilder />} />

          {/* Persona pages. Highest median bid of any cluster ($10.84). */}
          <Route path='/for'                 element={<PersonaHub />} />
          <Route path='/for/salons'          element={<PersonaPage />} />
          <Route path='/for/dentists'        element={<PersonaPage />} />
          <Route path='/for/restaurants'     element={<PersonaPage />} />
          <Route path='/for/nonprofits'      element={<PersonaPage />} />
          <Route path='/for/photographers'   element={<PersonaPage />} />
          <Route path='/for/churches'        element={<PersonaPage />} />
          <Route path='/for/gyms'            element={<PersonaPage />} />
          <Route path='/for/musicians'       element={<PersonaPage />} />
          <Route path='/for/law-firms'       element={<PersonaPage />} />
          <Route path='/for/schools'         element={<PersonaPage />} />
          <Route path='/for/consultants'     element={<PersonaPage />} />
          <Route path='/for/insurance-agents' element={<PersonaPage />} />
          <Route path='/for/financial-advisors' element={<PersonaPage />} />
          <Route path='/for/chiropractors'   element={<PersonaPage />} />
          <Route path='/for/med-spas'        element={<PersonaPage />} />
          <Route path='/for/car-dealerships' element={<PersonaPage />} />
          <Route path='/for/accountants'     element={<PersonaPage />} />
          <Route path='/for/contractors'     element={<PersonaPage />} />
          <Route path='/for/plumbers'        element={<PersonaPage />} />
          <Route path='/for/startups'        element={<PersonaPage />} />
          <Route path='/for/authors'         element={<PersonaPage />} />
          <Route path='/for/bakeries'        element={<PersonaPage />} />
          <Route path='/for/agencies'        element={<PersonaPage />} />
          <Route path='/for/small-business'  element={<PersonaPage />} />
          <Route path='/for/real-estate'     element={<PersonaPage />} />

          {/* /tools — the hub. Fixes a 403: the prerenderer creates the directory
              but Options -Indexes turned the bare path into a permission error. */}
          <Route path='/tools' element={<ToolsHub />} />

          {/* Name generators — 29,200/mo across five pages, every one KD <= 8.
              Three of them are deliberate MERGES of keyword pairs that share a
              SERP; see the header of NameGeneratorPage.js. */}
          <Route path='/tools/instagram-name-generator'             element={<NameGeneratorPage />} />
          <Route path='/tools/tiktok-username-generator'            element={<NameGeneratorPage />} />
          <Route path='/tools/youtube-name-generator'               element={<NameGeneratorPage />} />
          <Route path='/tools/aesthetic-username-generator'         element={<NameGeneratorPage />} />
          <Route path='/tools/podcast-name-generator'               element={<NameGeneratorPage />} />

          {/* LinkedIn text formatter — CPC $59.05, KD 9, wall 14. Four keywords
              (text formatter / formatter / post formatter / bold text) return the
              same SERP, so this one page targets all of them. */}
          <Route path='/tools/linkedin-text-formatter'              element={<LinkedInFormatterPage />} />

          {/* Emoji translator — 2,900/mo, KD 12, wall 2, no AI Overview.
              Hootsuite and Pallyy both rank here, which is why this passed the
              audience test the other novelty tools failed. */}
          <Route path='/tools/emoji-translator'                     element={<EmojiTranslatorPage />} />

          {/* Grid planner — 1,600/mo combined, wall 0. Smallest volume built so
              far, chosen because it is the closest tool to the product: a grid
              planner IS a scheduling preview. `grid maker for instagram` (6,600)
              is a different tool — an image splitter — and is NOT targeted. */}
          <Route path='/tools/instagram-grid-planner'               element={<GridPlannerPage />} />

          {/* Best-time cluster — 2.55M searches at competition 1-2. */}
          <Route path='/tools/instagram-post-size'                  element={<SizePage />} />
          <Route path='/tools/linkedin-banner-size'                 element={<SizePage />} />
          <Route path='/tools/youtube-banner-size'                  element={<SizePage />} />
          <Route path='/tools/facebook-event-cover-photo-size'      element={<SizePage />} />
          <Route path='/tools/content-calendar-template'            element={<ContentCalendarTemplatePage />} />
          <Route path='/tools/social-media-report-template'         element={<SocialReportTemplatePage />} />
          <Route path='/tools/instagram-caption-generator'          element={<CaptionGeneratorPage />} />
          <Route path='/tools/tiktok-caption-generator'             element={<CaptionGeneratorPage />} />
          <Route path='/tools/youtube-description-generator'        element={<CaptionGeneratorPage />} />
          <Route path='/tools/facebook-post-generator'              element={<CaptionGeneratorPage />} />
          <Route path='/tools/instagram-bio-generator'              element={<BioGeneratorPage />} />
          <Route path='/tools/tiktok-bio-generator'                 element={<BioGeneratorPage />} />
          <Route path='/tools/linkedin-bio-generator'               element={<BioGeneratorPage />} />
          <Route path='/tools/engagement-rate-calculator'           element={<EngagementRatePage />} />
          <Route path='/tools/engagement-rate-calculator/instagram' element={<EngagementRatePage />} />
          <Route path='/tools/engagement-rate-calculator/tiktok'    element={<EngagementRatePage />} />
          <Route path='/best-time-to-post'           element={<BestTimePage />} />
          <Route path='/best-time-to-post/instagram' element={<BestTimePage />} />
          <Route path='/best-time-to-post/tiktok'    element={<BestTimePage />} />
          <Route path='/video-photo-editor' element={<VideoPhotoEditor />} />
          <Route path='/calendar-view'      element={<CalendarView />} />

          {/* Blog */}
          <Route path='/blog'        element={<BlogHome />} />
          <Route path='/blog/:slug'  element={<BlogPost />} />

          {/* Pricing, contact & legal */}
          <Route path='/plans'    element={<PlansPricing />} />
          <Route path='/contact'  element={<ContactUs />} />
          <Route path='/privacy'  element={<PrivacyPolicy />} />
          <Route path='/terms'    element={<TermsPolicy />} />
          <Route path="/safety-policy" element={<SafetyPolicy />} /> 
          {/* --- 2. ADD THE NEW ROUTE FOR YOUR PAGE --- */}
          <Route path='/api-documentation' element={<ApiDocumentationPage />} />
          <Route path='/email-domain-setup' element={<EmailDomainSetupPage />} />


          {/* Company pages */}
          <Route path='/about'            element={<About />} />
          <Route path='/careers'            element={<Careers />} />
          <Route path='/press'              element={<Press />} />
          <Route path='/affiliate-program' element={<AffiliateProgram />} />

          {/* Catch-all. Without this an unmatched URL rendered header and footer
              with an empty middle, under the homepage's title — a blank HTTP 200,
              which Google reads as a soft 404 and which tells the visitor nothing.
              Kept inside Layout so the 404 keeps the nav and footer. */}
          <Route path='*' element={<NotFound />} />
        </Route>

        {/* ── Dashboard (protected) ── */}
        <Route
          path='/dashboard'
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to='quickstart' replace />} />

          {/* Quickstart hub (landing) */}
          <Route path='quickstart'     element={<QuickstartPage />} />

          {/* Main sections */}
          <Route path='post'          element={<Post />} />
          <Route path='accounts'       element={<Accounts />} />
          <Route path='files'          element={<Files />} />
          <Route path='link-in-bio'    element={<LinkInBioPage />} />

          {/* AI Studio subtree */}
          <Route path='studio' element={<StudioLayout />}>
            <Route index           element={<Navigate to='bulk' replace />} />
            <Route path='bulk'      element={<AIBulkStudioPage />} />
            <Route path='bulk/:jobId' element={<AIBulkStudioPage />} />
          </Route>

          {/* Marketing subtree */}
          <Route path='marketing' element={<MarketingLayout />}>
            <Route index            element={<Navigate to='crm' replace />} />
            <Route path='crm'        element={<CRMPage />} />
            <Route path='email' element={<EmailLayout />}>
              <Route index             element={<Navigate to='campaigns' replace />} />
              <Route path='lists'       element={<EmailLists />} />
              <Route path='campaigns'   element={<EmailMarketingPage />} />
              <Route path='automations' element={<EmailAutomations />} />
              <Route path='settings'    element={<EmailSettings />} />
            </Route>
          </Route>

          <Route path='profile'        element={<Profile />} />
          <Route path='admin'          element={<AdminPage />} />
          <Route path='calendar'       element={<Calendar />} />
          <Route path='analytics'      element={<AnalyticsPage />} />
          <Route path='plans'          element={<PlansPage />} />
          <Route path='credits'        element={<CreditsPage />} />
          <Route path='video-credits'  element={<VideoCreditsPage />} />

          {/* ── Settings subtree ── */}
          <Route path='settings' element={<SettingsLayout />}>
            <Route index              element={<AccountSettingsPage />} />
            <Route path='account'      element={<AccountSettingsPage />} />
            <Route path='subscription' element={<SubscriptionSettingsPage />} />
            <Route path='api-keys'     element={<ApiKeysPage />} />
            <Route path='hashtags'     element={<HashtagSettingsPage />} />
            <Route path='logout'       element={<LogoutPage />} />
          </Route>
        </Route>

      </Routes>
      </ErrorBoundary>
    </NotificationProvider>
  )
}

export default App
